import React from 'react';
import { useAppSelector } from '../../../store';
import {
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import MyTableBoldCell from '../../../components/material-ui/adaptive/MyTableBoldCell';
import { useTranslation } from 'react-i18next';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import CusSiteDateTime from '../../../components/material-ui/CusSiteDateTime';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import CurrencyValue from '../../../components/material-ui/CurrencyValue';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import ReportBonusTotal from './ReportBonusTotal';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import ErrorBoundary from '../../../components/material-ui/error_boundary/ErrorBoundary';
import { stickyCellRight } from '../../../components/bonus/ui/styles';
import ShowContentWitchEmpty from '../../../components/material-ui/show_content_empty/ShowContentWitchEmpty';
import LoaderContainer from '../../../components/material-ui/loader_container/LoaderContainer';

export default function ReportBonusTable({ loader, empty }) {
  const { t } = useTranslation();
  const bonusesInfo = useAppSelector((state) => state.reports.bonuses.info);
  const currency = useAppSelector((state) => state.reports.bonuses.payload?.currency);

  const dataBonuses = Object.keys(bonusesInfo);

  const MiniTypography = ({ text }) => <TypographyText fontSize={11}>{t(text)} </TypographyText>;

  const TitleTypography = ({ title, tooltip, minWidth = '80px' }) => (
    <Stack direction={'row'} gap={0.5} justifyContent={'center'} sx={{ minWidth: minWidth }}>
      <Typography fontWeight={900} fontSize={13}>
        {t(title)}
      </Typography>
      <CusTooltip text={tooltip} />
    </Stack>
  );

  const CountValue = ({ value, count }) => (
    // <ShowContainer condition={value}>
    <Stack direction={'row'} gap={1}>
      <CurrencyValue
        defaultValue="0"
        value={value}
        currency={currency}
        fontWeight={900}
        fontSize={12}
      />{' '}
      <TypographyText color="textSecondary">({count})</TypographyText>
    </Stack>
    // </ShowContainer>
  );

  return (
    <LoaderContainer loader={loader} isBox>
      <ShowContentWitchEmpty
        condition={dataBonuses.length}
        conditionPlug={empty}
        plugText="No entries found. Try changing the filters.">
        <Stack>
          <TableContainer
            sx={{
              overflow: 'auto',
              maxHeight: { xs: '100svh', sm: 'calc(100svh - 180px - 200px )' }
            }}>
            <Table>
              <TableHead>
                {/* Первая строка заголовка - основные колонки */}
                <TableRow>
                  <MyTableBoldCell isLess rowSpan={2} sx={{ pr: 1, verticalAlign: 'sub' }}>
                    {t('Date')}
                  </MyTableBoldCell>

                  <MyTableBoldCell isLess colSpan={3} align="center" sx={{ verticalAlign: 'sub' }}>
                    <TitleTypography
                      title={'Jackpots'}
                      tooltip={'The amount and number of jackpots awarded to players'}
                    />
                  </MyTableBoldCell>

                  <MyTableBoldCell isLess rowSpan={2} sx={{ verticalAlign: 'sub' }}>
                    <Stack gap={0.75}>
                      <TypographyText fontWeight={900}>
                        <TitleTypography
                          title={'Welcome'}
                          tooltip={
                            'The amount and number of bonuses issued and wagered are calculated as the sum of all bonuses in the "Welcome Package." The wagered amount received from Free Spins in the "Welcome Bonus" is also taken into account.'
                          }
                        />
                      </TypographyText>
                      <MiniTypography text={'number of issued'} />
                      <MiniTypography text={'number of played'} />
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell isLess rowSpan={2} sx={{ verticalAlign: 'sub' }}>
                    <Stack gap={0.75}>
                      <TitleTypography
                        title={'Free spins'}
                        tooltip={
                          'Free Spins take into account the number of bonuses issued (received by players) and the wagered amount, i.e., the amount "cleared" from wagering and credited to the main balance available for withdrawal.'
                        }
                      />
                      <MiniTypography text={'number of issued'} />
                      <MiniTypography text={'number of played'} />
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell isLess rowSpan={2} sx={{ verticalAlign: 'sub' }}>
                    <Stack gap={0.75}>
                      <TitleTypography
                        title={'Cashback'}
                        tooltip={
                          'Cashback takes into account the amount and number of cashback issued and wagered on all days of the week.'
                        }
                      />
                      <MiniTypography text={'number of issued'} />
                      <MiniTypography text={'number of played'} />
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell isLess rowSpan={2} sx={{ verticalAlign: 'sub' }}>
                    <Stack gap={0.75}>
                      <TitleTypography
                        title={'Promocodes'}
                        tooltip={
                          'Promo codes are only taken into account when activated by players.'
                        }
                      />
                      <MiniTypography text={'number of issued'} />
                      <MiniTypography text={'number of played'} />
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell isLess rowSpan={2} sx={{ verticalAlign: 'sub' }}>
                    <Stack gap={0.75}>
                      <TitleTypography
                        title={'For launching a bot'}
                        minWidth="110px"
                        tooltip={
                          '"For launching a bot" - successfully credited and wagered bonuses are taken into account.'
                        }
                      />
                      <MiniTypography text={'number of issued'} />
                      <MiniTypography text={'number of played'} />
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell isLess rowSpan={2} sx={{ verticalAlign: 'sub' }}>
                    <Stack gap={0.75}>
                      <TitleTypography
                        title={'For a phone in the bot'}
                        minWidth="110px"
                        tooltip={
                          '"For a phone in the bot" - successfully credited and wagered bonuses are taken into account.'
                        }
                      />

                      <MiniTypography text={'number of issued'} />
                      <MiniTypography text={'number of played'} />
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell
                    isLess
                    rowSpan={2}
                    sx={{
                      pl: 2,
                      backgroundColor: (theme) => theme.palette.grey[100],
                      ...stickyCellRight
                    }}>
                    {t('Total, amount')}
                  </MyTableBoldCell>
                </TableRow>

                {/* Вторая строка заголовка - подколонки */}
                <TableRow>
                  {/* Джекпоты */}
                  <MyTableBoldCell isLess>{t('Mini')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Major')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Grand')}</MyTableBoldCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataBonuses.map((bonusData) => {
                  const bonusInfo = bonusesInfo[bonusData];

                  const miniJack = bonusInfo.jackpot?.mini || [];
                  const majorJack = bonusInfo.jackpot?.major || [];
                  const grandJack = bonusInfo.jackpot?.grand || [];

                  const welcome = bonusInfo.welcome || [];
                  const cashback = bonusInfo.cashback || [];
                  const freespin = bonusInfo.freespin || [];
                  const promocode = bonusInfo.promocode || [];
                  const startBot = bonusInfo.startBot || [];
                  const botPhone = bonusInfo.botPhone || [];

                  const bonusTotal =
                    miniJack[1] +
                    majorJack[1] +
                    grandJack[1] +
                    welcome[1] +
                    cashback[1] +
                    freespin[1] +
                    promocode[1] +
                    startBot[1] +
                    botPhone[1];

                  return (
                    <TableRow
                      key={bonusData}
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <MyTableCell isLess>
                        <CusSiteDateTime date={bonusData} format="DD.MM.YYYY" />
                      </MyTableCell>
                      <MyTableCell isLess type="numeric">
                        <CountValue value={miniJack[1]} count={miniJack[0]} />
                      </MyTableCell>
                      <MyTableCell isLess type="numeric">
                        <CountValue value={majorJack[1]} count={majorJack[0]} />
                      </MyTableCell>
                      <MyTableCell isLess type="numeric">
                        <CountValue value={grandJack[1]} count={grandJack[0]} />
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} alignItems={'end'}>
                          <CountValue value={welcome[1]} count={welcome[0]} />
                          <CountValue value={welcome[3]} count={welcome[2]} />
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} alignItems={'end'}>
                          <CountValue value={freespin[1]} count={freespin[0]} />
                          <CountValue value={freespin[3]} count={freespin[2]} />
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} alignItems={'end'}>
                          <CountValue value={cashback[1]} count={cashback[0]} />
                          <CountValue value={cashback[3]} count={cashback[2]} />
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} alignItems={'end'}>
                          <CountValue value={promocode[1]} count={promocode[0]} />
                          <CountValue value={promocode[3]} count={promocode[2]} />
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} alignItems={'end'}>
                          <CountValue value={startBot[1]} count={startBot[0]} />
                          <CountValue value={startBot[3]} count={startBot[2]} />
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} alignItems={'end'}>
                          <CountValue value={botPhone[1]} count={botPhone[0]} />
                          <CountValue value={botPhone[3]} count={botPhone[2]} />
                        </Stack>
                      </MyTableCell>
                      <MyTableCell
                        isLess
                        type="numeric"
                        sx={{
                          pl: 2,
                          backgroundColor: (theme) => theme.palette.grey[100],
                          ...stickyCellRight,
                          zIndex: 2
                        }}>
                        <CurrencyValue
                          value={bonusTotal}
                          defaultValue="0"
                          currency={currency}
                          fontSize={12}
                          fontWeight={900}
                        />
                      </MyTableCell>
                    </TableRow>
                  );
                })}
                <ErrorBoundary>
                  <ReportBonusTotal CountValue={CountValue} />
                </ErrorBoundary>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </ShowContentWitchEmpty>{' '}
    </LoaderContainer>
  );
}
